import clsx from 'clsx';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSessionCommentCreate } from 'src/generated/endpoint';
import { ResponseUserDto, SessionComment } from 'src/generated/model';
import { twMerge } from 'tailwind-merge';
import { ButtonV2 } from '../common/ButtonV2';
import { IBBlank } from '../common/IBBlank';
import { Typography } from '../common/Typography';
import { ActivityComment } from './ActivityComment';

interface ActivityCommentsProps {
  id: number;
  comments: SessionComment[];
  readonly?: boolean;
  disabled?: boolean;
  className?: string;
  refetch: () => void;
  user: ResponseUserDto;
}

export const ActivityComments: FC<ActivityCommentsProps> = ({
  id,
  comments,
  user,
  readonly = false,
  disabled = false,
  className,
  refetch,
}) => {
  const [content, setContent] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const { mutate: createSessionComment, isLoading: createSessionCommentLoading } = useSessionCommentCreate({
    mutation: {
      onSuccess: () => {
        refetch();
        setContent('');
      },
    },
  });

  const onSubmit = useCallback(() => {
    if (!content.trim() || createSessionCommentLoading) return;

    createSessionComment({
      params: { sessionId: id },
      data: { content: content },
    });
  }, [content, createSessionCommentLoading, createSessionComment, id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  return (
    <div className={twMerge('relative flex h-full flex-col justify-between gap-6', className)}>
      {createSessionCommentLoading ? (
        <IBBlank type="section" />
      ) : (
        <div className="flex h-full flex-grow flex-col gap-6" ref={scrollRef}>
          <div
            className={clsx(
              'relative flex flex-col justify-between gap-4 rounded-lg border border-primary-gray-200 p-4 focus:outline-none focus:ring-0',
              {
                'bg-white': readonly,
                'bg-primary-gray-100': disabled,
                'cursor-not-allowed': readonly || disabled,
                'cursor-pointer': !readonly && !disabled,
                'border-primary-gray-700': isFocused,
              },
            )}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className={clsx(
                `h-16 resize-none border-none p-0 text-15 text-primary-gray-900 placeholder-primary-gray-400 caret-primary-blue-800 focus:border-none focus:text-primary-gray-700 focus:ring-0 disabled:text-gray-400`,
                {
                  'bg-white': readonly,
                  'bg-primary-gray-100': disabled,
                  'cursor-not-allowed': readonly || disabled,
                  'cursor-pointer': !readonly && !disabled,
                },
              )}
              disabled={disabled}
              readOnly={readonly}
              placeholder={`댓글은 담당 선생님께만 노출됩니다`}
            />
            <nav className="flex flex-row items-center justify-between">
              <Typography variant="caption" className="text-primary-gray-500">
                {content.length}&nbsp;/&nbsp;1,000
              </Typography>
              <ButtonV2
                variant="solid"
                color="orange100"
                size={32}
                disabled={disabled || content.length === 0}
                onClick={onSubmit}
              >
                저장
              </ButtonV2>
            </nav>
          </div>

          {comments &&
            comments?.length > 0 &&
            [...comments]
              .reverse()
              .map((comment) => <ActivityComment key={comment.id} comment={comment} user={user} refetch={refetch} />)}
        </div>
      )}
    </div>
  );
};
